import {
  Icon,
  IconFile,
  IconFileMusic,
  IconFileSpreadsheet,
  IconFileText,
  IconFileTypeCsv,
  IconFileTypeDoc,
  IconFileTypeDocx,
  IconFileTypeHtml,
  IconFileTypePdf,
  IconFileTypeXls,
  IconFileZip,
  IconHeadphones,
  IconJson,
  IconPhoto,
  IconQuestionMark,
  IconVideo,
} from '@tabler/icons-react';
import { FileTypes } from 'database';
import { format } from 'date-fns';
import {
  Badge,
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  cn,
} from 'ui';
import { mimetypes } from './FileInput';
import React, { memo, useState } from 'react';
import { TextFilePreview } from './TextFilePreview';

export const iconsByFileType: {
  [K in FileTypes]: Icon;
} = {
  pdf: IconFileTypePdf,
  other: IconQuestionMark,
  audio: IconFileMusic,
  document: IconFile,
  image: IconPhoto,
  video: IconVideo,
};

// Íconos específicos por mimetype exacto. Tienen prioridad sobre el ícono
// genérico por `FileTypes` (ver `iconsByFileType`) — permite distinguir, p. ej.,
// un Word de un Excel de un ZIP, que de otro modo caerían todos en `document`.
export const iconsByMimetype: {
  [mimetype: string]: Icon;
} = {
  'application/zip': IconFileZip,
  'application/x-zip-compressed': IconFileZip,
  'text/csv': IconFileTypeCsv,
  'text/html': IconFileTypeHtml,
  'application/json': IconJson,
  // Word (.doc / .docx)
  'application/msword': IconFileTypeDoc,
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
    IconFileTypeDocx,
  // Excel (.xls / .xlsx)
  'application/vnd.ms-excel': IconFileTypeXls,
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
    IconFileSpreadsheet,
};

export const getFileType = (fileMimetype: string | null): FileTypes => {
  if (!fileMimetype) return 'other';

  for (const type in mimetypes) {
    const list = mimetypes[type as keyof typeof mimetypes];

    if (list.includes(fileMimetype)) {
      return type as FileTypes;
    }
  }

  return 'other';
};

export const FileCardList = ({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) => {
  return (
    <div
      className={cn(
        'grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4',
        className,
      )}
    >
      {children}
    </div>
  );
};

export const FileCard = React.forwardRef<
  React.ElementRef<typeof Card>,
  React.ComponentPropsWithoutRef<typeof Card>
>(({ className, ...props }, ref) => {
  return (
    <Card
      {...props}
      className={cn(
        'relative group h-full cursor-pointer transition-all',
        className,
      )}
      ref={ref}
    />
  );
});

const FilePreview = ({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) => {
  return <div className={cn('relative', className)}>{children}</div>;
};

const ImagePreviewWithZoom = memo(
  ({
    previewURL,
    imageClassName,
  }: {
    previewURL: string;
    imageClassName?: string;
  }) => {
    const [zoom, setZoom] = useState(1);
    const [position, setPosition] = useState({ x: 0, y: 0 });
    const [isDragging, setIsDragging] = useState(false);
    const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
    const minZoom = 0.5;
    const maxZoom = 5;

    const handleWheel = React.useCallback((e: React.WheelEvent) => {
      e.preventDefault();
      setZoom((prevZoom) => {
        const newZoom = prevZoom - e.deltaY * 0.001;
        return Math.min(Math.max(newZoom, minZoom), maxZoom);
      });
    }, []);

    const handleMouseDown = React.useCallback(
      (e: React.MouseEvent) => {
        if (zoom > 1) {
          setIsDragging(true);
          setDragStart({ x: e.clientX, y: e.clientY });
        }
      },
      [zoom],
    );

    const handleMouseMove = React.useCallback(
      (e: React.MouseEvent) => {
        if (isDragging) {
          const dx = e.clientX - dragStart.x;
          const dy = e.clientY - dragStart.y;
          setPosition((prev) => ({ x: prev.x + dx, y: prev.y + dy }));
          setDragStart({ x: e.clientX, y: e.clientY });
        }
      },
      [isDragging, dragStart],
    );

    const handleMouseUp = React.useCallback(() => {
      setIsDragging(false);
    }, []);

    // Reset position when zoom returns to 1
    React.useEffect(() => {
      if (zoom <= 1) {
        setPosition({ x: 0, y: 0 });
      }
    }, [zoom]);

    return (
      <div
        className='h-full w-full overflow-hidden'
        onWheel={handleWheel}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        style={{
          cursor: isDragging ? 'grabbing' : zoom > 1 ? 'grab' : 'default',
        }}
      >
        <img
          onError={({ currentTarget }) => {
            currentTarget.onerror = null;
            currentTarget.src = '/images/preview-placeholder.jpg';
          }}
          className={cn(
            'h-full w-full object-contain',
            'transition-transform duration-150 ease-out',
            imageClassName,
          )}
          style={{
            transform: `scale(${zoom}) translate(${position.x / zoom}px, ${
              position.y / zoom
            }px)`,
          }}
          src={previewURL}
          alt='File preview'
          draggable='false'
        />
      </div>
    );
  },
);

const FilePreviewContent = ({
  previewURL = '/images/preview-placeholder.jpg',
  type,
  imageClassName,
  mode = 'full',
}: {
  previewURL?: string;
  type: FileTypes;
  imageClassName?: string;
  mode?: 'thumbnail' | 'full';
}) => {
  if (mode === 'thumbnail') {
    return (
      <img
        onError={({ currentTarget }) => {
          currentTarget.onerror = null;
          currentTarget.src = '/images/preview-placeholder.jpg';
        }}
        className={cn('h-full w-full object-top object-cover', imageClassName)}
        src={previewURL}
      />
    );
  }

  switch (type) {
    case 'pdf':
      return (
        <div className='w-full h-full flex items-center justify-center z-0'>
          <iframe
            src={previewURL}
            className='w-full h-full rounded-md z-0'
            loading='lazy'
          />
        </div>
      );
    case 'video':
      return (
        <video
          className={cn('h-full w-full rounded-md object-top', imageClassName)}
          src={previewURL}
          muted
          loop
          onMouseOver={(e) => e.currentTarget.play()}
          onMouseOut={(e) => e.currentTarget.pause()}
          poster={previewURL}
        >
          <source src={previewURL} type='video/mp4' />
          Your browser does not support the video tag.
        </video>
      );
    case 'audio':
      return (
        <div className='h-full w-full bg-slate-50 flex items-center justify-center relative'>
          <audio className='w-full absolute bottom-0' controls src={previewURL}>
            Your browser does not support the audio element.
          </audio>
          <IconHeadphones className='h-20 w-20 text-primary' />
        </div>
      );
    case 'document':
      const getFileExtension = (url: string) => {
        const baseUrl = url.split('?')[0];
        return baseUrl?.split('.').pop()?.toLowerCase();
      };

      const fileExtension = previewURL ? getFileExtension(previewURL) : null;

      if (fileExtension && ['csv', 'txt'].includes(fileExtension)) {
        return <TextFilePreview previewURL={previewURL} />;
      }

      return (
        <div className='relative w-full h-full'>
          <object
            data={previewURL}
            type='application/msword'
            className='w-full h-full rounded-md absolute'
          >
            <iframe
              src={`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(
                previewURL,
              )}`}
              className='w-full h-full absolute'
            >
              <div className='h-full w-full bg-slate-50 flex items-center justify-center'>
                <IconFileText className='h-20 w-20 text-primary' />
              </div>
            </iframe>
          </object>
        </div>
      );
    default:
      if (type === 'image') {
        return (
          <ImagePreviewWithZoom
            key={previewURL}
            previewURL={previewURL}
            imageClassName={imageClassName}
          />
        );
      }

      return (
        <img
          onError={({ currentTarget }) => {
            currentTarget.onerror = null;
            currentTarget.src = '/images/preview-placeholder.jpg';
          }}
          className={cn(
            'h-full w-full object-contain',
            'transition-transform duration-150 ease-out',
            imageClassName,
          )}
          src={previewURL}
          alt='File preview'
        />
      );
  }
};

const MemoizedFilePreviewContent = memo(
  FilePreviewContent,
  (prevProps, nextProps) => {
    return (
      prevProps.previewURL === nextProps.previewURL &&
      prevProps.type === nextProps.type &&
      prevProps.mode === nextProps.mode
    );
  },
);

export const FileCardPreview = ({
  type,
  previewURL,
  className,
  imageClassName,
  mode = 'full',
}: {
  type: FileTypes;
  previewURL?: string;
  className?: string;
  imageClassName?: string;
  mode?: 'thumbnail' | 'full';
}) => {
  return (
    <FilePreview className={cn(className)}>
      {type === 'audio' && mode === 'thumbnail' ? (
        <div className='h-full w-full bg-slate-50 flex items-center justify-center'>
          <IconHeadphones className='h-20 w-20 text-primary' />
        </div>
      ) : (
        <MemoizedFilePreviewContent
          previewURL={previewURL || ''}
          type={type}
          imageClassName={imageClassName}
          mode={mode}
        />
      )}
    </FilePreview>
  );
};

export const FileCardContent = ({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) => {
  return (
    <CardContent className={cn('flex items-center justify-center', className)}>
      {children}
    </CardContent>
  );
};
export const FileCardTag = ({ children }: { children: React.ReactNode }) => {
  return (
    <TooltipProvider delayDuration={0}>
      <Tooltip>
        <TooltipTrigger>
          <Badge
            className='text-xs bg-card group-hover:text-secondary group-hover:bg-primary'
            variant={'outline'}
          >
            <span className='max-w-[6rem] w-full truncate'>{children}</span>
          </Badge>
        </TooltipTrigger>
        <TooltipContent>
          <p className='text-xs'>{children}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export const FileCardTagList = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  return (
    <div className='flex flex-row gap-3 flex-wrap'>
      <div className='absolute right-0 -top-3'>
        <div className='flex flex-row flex-wrap gap-2'>{children}</div>
      </div>
    </div>
  );
};

export const FileCardFilename = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  return (
    <CardTitle className='flex-row space-x-4 items-start truncate block !my-0'>
      {children}
    </CardTitle>
  );
};

export const FileCardIcon = ({
  mimetype,
  lookupMode = 'calculate',
  className,
}: {
  mimetype: string;
  lookupMode?: 'calculate' | 'rely';
  className?: string;
}) => {
  // En modo `calculate`, un ícono específico por mimetype tiene prioridad; si no
  // hay match, se cae al ícono genérico por `FileTypes`. El modo `rely` (recibe
  // una clave de `FileTypes`) se mantiene igual → retrocompatible.
  const Icon =
    lookupMode === 'calculate'
      ? iconsByMimetype[mimetype] ?? iconsByFileType[getFileType(mimetype)]
      : iconsByFileType[mimetype as keyof typeof iconsByFileType];

  if (!Icon) return null;

  return (
    <Icon
      className={cn('w-2 h-2 mr-2 flex-shrink-0 text-primary', className)}
    />
  );
};

export const FileCardHeader = ({ children }: { children: React.ReactNode }) => {
  return (
    <CardHeader className='flex flex-row items-center'>{children}</CardHeader>
  );
};

export const FileCardAuthor = ({
  uploadedByEmail,
  createdAt,
}: {
  uploadedByEmail?: string;
  createdAt: string | Date;
}) => {
  return (
    <div>
      <p className='text-muted-foreground'>{uploadedByEmail} </p>
      <time className='text-muted-foreground'>
        {format(new Date(createdAt), 'dd/MM/yyyy HH:mm')}
      </time>
    </div>
  );
};

export const FileCardFooter = ({ children }: { children: React.ReactNode }) => {
  return (
    <CardFooter className='flex flex-col text-xs space-y-4 items-start'>
      {children}
    </CardFooter>
  );
};
